import { useQuery } from "@tanstack/react-query";
import { detectTenant } from "@/shared/lib/utils";
import { logger } from '@/shared/lib/logger';
import {
  validateTenantConfig,
  type TenantConfig,
} from "@/config/schemas/tenantConfig.schema";

/**
 * Fetches the configuration for a "default" tenant from a local file.
 * This serves as a universal fallback.
 */
const fetchDefaultConfigFromMocks = async (): Promise<TenantConfig> => {
  try {
    const response = await fetch(`/tenants/default.json`);
    if (!response.ok) {
      throw new Error(`Default config file not found or invalid.`);
    }
    const data = await response.json();
    return validateTenantConfig(data);
  } catch (error) {
    // If the default mock itself fails, we must throw, as the app is unusable.
    throw new Error("Could not load default configuration.");
  }
}

/**
 * Fetches tenant configuration from static files.
 * - First tries to load tenant-specific config from `/tenants/{tenantId}.json`
 * - Falls back to `/tenants/default.json` if tenant-specific file not found
 * - Works in both development and production (CloudFront/S3)
 */
const fetchTenantConfig = async (tenantId: string): Promise<TenantConfig> => {
  // Try to load tenant-specific configuration
  try {
    const response = await fetch(`/tenants/${tenantId}.json`);
    if (response.ok) {
      const data = await response.json();
      logger.debug(`✅ Loaded tenant config for '${tenantId}'`);
      return validateTenantConfig(data);
    }
    // If specific tenant config not found, fall back to default
    logger.warn(`⚠️ Config for tenant '${tenantId}' not found. Falling back to default.`);
    return fetchDefaultConfigFromMocks();
  } catch (error) {
    logger.warn(`⚠️ Error loading config for '${tenantId}'. Falling back to default.`, error);
    return fetchDefaultConfigFromMocks();
  }
};


// Hook to get tenant configuration
export const useTenant = () => {
  const tenantId = detectTenant();

  const {
    data: tenant,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => fetchTenantConfig(tenantId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Retry once on failure
  });

  return {
    tenant: loading ? null : tenant,
    tenantId,
    loading,
    error: error ? (error as Error).message : null,
    refetch,
  };
};
